import type { RailLayout } from "@/data/rail-branches";
import type { LocationRun } from "./runs";
import { characterGroupsAtPlayhead } from "../../character/ActiveCharacterGroup/atPlayhead";
import { markerRailPose } from "../../rail/RailPose/pose";
import { stayAt } from "../../rail/StayAt/stay";
import { placeGroupsAt } from "../../rail/RailPlaceGroups/groups";
import { branchForSiteRun } from "./siteBranches";

/** Site box rides the cast while the scene plays or the branch still has cohorts. */
export function siteRunFollowsCastColumn(
  rail: RailLayout,
  run: LocationRun,
  playheadMs: number,
): boolean {
  if (playheadMs >= run.startMs && playheadMs < run.endMs) {
    return true;
  }
  const branch = branchForSiteRun(run, rail);
  if (branch === undefined) {
    return false;
  }
  if (playheadMs < branch.startMs || playheadMs >= branch.endMs) {
    return false;
  }
  return characterGroupsAtPlayhead(rail, playheadMs).some(
    (cohort) => cohort.branchId === branch.id,
  );
}

/** Site label sits halfway between the centers of the groups on this line. */
export function siteGroupsMidpointDx(
  run: LocationRun,
  rail: RailLayout,
  playheadMs: number,
): number {
  const branch = branchForSiteRun(run, rail);
  if (branch === undefined) {
    return 0;
  }
  const centers: number[] = [];
  for (const group of placeGroupsAt(rail.stays, branch.rootPlaceId, playheadMs)) {
    const dx = memberCenterDx(rail, group.characterIds, playheadMs);
    if (dx !== null) {
      centers.push(dx);
    }
  }
  if (centers.length === 0) {
    return 0;
  }
  return centers.reduce((sum, dx) => sum + dx, 0) / centers.length;
}

/** Average horizontal seat of the people in one group. */
export function memberCenterDx(
  rail: RailLayout,
  characterIds: readonly string[],
  playheadMs: number,
): number | null {
  let sum = 0;
  let count = 0;
  for (const characterId of characterIds) {
    const pose = markerRailPose(characterId, playheadMs, rail);
    // Boxes center on the people. Vehicles have their own row above them.
    if (pose === null || stayAt(rail.stays, characterId, playheadMs)?.vehicle === true) {
      continue;
    }
    sum += pose.dx;
    count += 1;
  }
  if (count === 0) {
    return null;
  }
  return sum / count;
}
